import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, ExternalLink, Users } from "lucide-react";
import { getCompanySettings, updateCompanySettings, errMsg } from "@/lib/applyApi";
import { copyText } from "@/lib/clipboard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";

const MODES = [
  ["approval_required", "Approval Required", "Sellers submit an application; an admin must accept the horse before the contract."],
  ["open_submissions", "Open Submissions", "Sellers can start the contract and listing workflow without initial acceptance."],
];

export function SubmissionSettings() {
  const [c, setC] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    getCompanySettings().then(setC).catch(() => {});
  }, []);

  const save = async (changes) => {
    setBusy(true);
    try {
      setC(await updateCompanySettings(changes));
      toast.success("Seller settings saved");
    } catch (e) {
      toast.error(errMsg(e, "Could not save"));
    } finally {
      setBusy(false);
    }
  };

  if (!c) return null;
  const url = `${window.location.origin}/apply/${c.slug}`;
  return (
    <section data-testid="submission-settings" className="rounded-xl border border-stone-200 bg-white p-5">
      <h2 className="font-semibold text-stone-900 flex items-center gap-2"><Users className="h-4 w-4 text-amber-800" /> Seller Applications</h2>
      <p className="text-xs text-stone-500 mt-1 mb-4">Seller Submission Mode for {c.name}. Final ad approval is always required before publishing.</p>
      <RadioGroup value={c.submission_mode} onValueChange={(v) => save({ submission_mode: v })} disabled={busy} className="space-y-2">
        {MODES.map(([v, l, d]) => (
          <label key={v} htmlFor={`mode-${v}`} className={`flex items-start gap-3 rounded-lg border p-3 cursor-pointer ${c.submission_mode === v ? "border-amber-800 bg-amber-50/50" : "border-stone-200"}`}>
            <RadioGroupItem id={`mode-${v}`} data-testid={`mode-${v}`} value={v} className="mt-0.5" />
            <span><span className="block text-sm font-semibold text-stone-900">{l}</span><span className="block text-xs text-stone-500">{d}</span></span>
          </label>
        ))}
      </RadioGroup>

      <div className="mt-5">
        <label className="text-xs font-semibold text-stone-600">Public application link (for the website's “Sell Your Horse” button)</label>
        <div className="mt-1 grid grid-cols-1 sm:grid-cols-[1fr_auto_auto] gap-2">
          <Input data-testid="application-url" readOnly value={url} onFocus={(e) => e.target.select()} className="h-11 sm:h-10 font-mono text-base sm:text-sm bg-stone-50" />
          <Button data-testid="copy-application-url" variant="outline" onClick={() => copyText(url, "Application link copied")} className="h-11 sm:h-10 rounded-full"><Copy className="h-4 w-4 mr-1.5" />Copy</Button>
          <Button asChild variant="outline" className="h-11 sm:h-10 rounded-full"><a href={url} target="_blank" rel="noopener noreferrer"><ExternalLink className="h-4 w-4 mr-1.5" />Open</a></Button>
        </div>
      </div>

      <p className="mt-5 text-xs text-stone-500">The Privacy Policy is managed under Legal Documents below and shown at <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-amber-800 underline">/privacy-policy</a>.</p>
    </section>
  );
}
